import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import type { Benchmark, FeedbackView } from '../types'
import {
  REASON_MAX, reasonHint, consentNotice, askBody, stageOf, comparison, newestFirst, decisionWords, suggestions,
  disagreeKind, formWords, keepNewWords,
} from '../lib/feedback-view'
import { NOT_PRICED } from '../lib/not-priced'
import type { Side } from '../lib/feedback-view'
import { money } from '../lib/money'

/** How often the card checks on a re-match, and how many misses read as lost contact. */
const POLL_MS = 3000
const MISSES = 5

/**
 * HR feedback on a benchmark (STE-992): the form, the progress line, the
 * current and new answers side by side, and the record.
 *
 * Nothing here changes the benchmark. Keep the new match asks the server, and
 * the server refuses if the benchmark moved since HR asked. Until HR chooses,
 * Salary Ranges and Match a job go on using the current one.
 */
export function BenchmarkFeedback({ jdId, bench, open, onClose, onKept, suggested }: {
  jdId: string
  bench: Benchmark
  /** Whether the form is open. The Disagree button lives in the Result card and opens it. */
  open: boolean
  /**
   * A survey job picked with Suggest this job beside one of the closest jobs,
   * on a role that was not priced. It fills in the box; it is a suggestion to
   * the matcher, never a pairing.
   */
  suggested?: string
  onClose: () => void
  /** Called after Keep the new match, so the screen reads the new benchmark. */
  onKept: () => void
}) {
  const [view, setView] = useState<FeedbackView | null>(null)
  const [reason, setReason] = useState('')
  const [surveyJob, setSurveyJob] = useState('')
  const [sending, setSending] = useState(false)
  const [deciding, setDeciding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lost, setLost] = useState(false)
  const misses = useRef(0)

  const read = useCallback(async () => {
    try {
      const got = await api.getFeedback(jdId)
      setView(got)
      misses.current = 0
      setLost(false)
    } catch {
      misses.current += 1
      if (misses.current >= MISSES) setLost(true)
    }
  }, [jdId])

  // Read once on arrival, so a re-match started earlier is picked up again.
  useEffect(() => { void read() }, [read])

  // A job picked from the closest list fills the box, and the card is brought
  // into view, because the list it was picked from sits below it.
  const formRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!suggested) return
    setSurveyJob(suggested)
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [suggested, open])

  const stage = stageOf(view)
  useEffect(() => {
    if (stage !== 'running') return
    const t = setInterval(() => { void read() }, POLL_MS)
    return () => clearInterval(t)
  }, [stage, read])

  const send = async () => {
    const problem = reasonHint(reason)
    if (problem) { setError(problem); return }
    setSending(true)
    setError(null)
    try {
      await api.askFeedback(jdId, askBody(reason, surveyJob, bench))
      await read()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be sent. Try again.')
      // One already waiting: show it rather than the form.
      await read()
    } finally {
      setSending(false)
    }
  }

  const decide = async (keep: 'new' | 'current') => {
    const p = view?.pending
    if (!p) return
    setDeciding(true)
    setError(null)
    try {
      if (keep === 'new') {
        await api.keepFeedback(jdId, p.feedbackId)
        onKept()
      } else {
        await api.discardFeedback(jdId, p.feedbackId)
      }
      await read()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That could not be saved. Try again.')
      await read()
    } finally {
      setDeciding(false)
    }
  }

  const tryAgain = () => {
    setReason(view?.pending?.reason ?? reason)
    setSurveyJob(view?.pending?.surveyJob ?? surveyJob)
    setView(view ? { ...view, pending: null } : view)
  }

  const words = formWords(disagreeKind(bench) ?? 'match')
  const notice = consentNotice(bench)
  const hint = reason.length > 0 ? reasonHint(reason) : null
  const offered = suggestions(bench, surveyJob)
  const history = newestFirst(view?.history ?? [])
  const pending = view?.pending ?? null
  const showForm = open && (stage === 'none' || stage === 'failed' || stage === 'stale')

  return (
    <>
      {stage === 'running' && (
        <section className="card card--pad" aria-live="polite">
          <h2>Matching this role again with your reason</h2>
          <p className="subline">
            {pending?.state === 'queued' ? 'Waiting its turn.' : 'Reading the role and the survey again.'}
            {' '}Until it finishes and you choose, nothing changes.
          </p>
          {lost && <p className="field__error" role="alert">Lost contact. Your request is safe and still running. This will keep checking.</p>}
        </section>
      )}

      {stage === 'failed' && pending && !showForm && (
        <section className="card card--pad" role="alert">
          <h2>The re-match could not be finished</h2>
          <p className="subline prose">{pending.failedReason ?? 'Nothing was changed.'}</p>
          <div className="actions"><button type="button" className="btn" onClick={tryAgain}>Try again</button></div>
        </section>
      )}

      {stage === 'stale' && pending && !showForm && (
        <section className="card card--pad" role="alert">
          <h2>Ask again</h2>
          <p className="subline prose">The role or its survey changed while this was running, so the new answer no longer fits. Ask again.</p>
          <div className="actions"><button type="button" className="btn" onClick={tryAgain}>Ask again</button></div>
        </section>
      )}

      {stage === 'ready' && pending?.answer && (
        <Choice
          current={bench}
          next={pending.answer}
          answer={pending.feedbackAnswer ?? ''}
          deciding={deciding}
          onKeepNew={() => void decide('new')}
          onKeepCurrent={() => void decide('current')}
          error={error}
        />
      )}

      {showForm && (
        <section className="card card--pad feedback-form" ref={formRef}>
          <h2>{words.title}</h2>
          <p className="subline prose">{words.intro}</p>
          <label className={`field ${hint ? 'field--error' : ''}`}>
            <span>{words.reasonLabel}</span>
            <textarea
              rows={4}
              value={reason}
              maxLength={REASON_MAX + 200}
              onChange={(e) => setReason(e.target.value)}
              placeholder={words.reasonPlaceholder}
            />
            <span className={`hint ${reason.trim().length > REASON_MAX ? 'hint--bad' : ''}`}>
              {reason.trim().length.toLocaleString('en-US')} of {REASON_MAX.toLocaleString('en-US')} characters
            </span>
            {hint && <span className="field__error">{hint}</span>}
          </label>
          <label className="field">
            <span>Survey job you think fits (optional)</span>
            <input
              type="text"
              list={`fb-jobs-${jdId}`}
              value={surveyJob}
              onChange={(e) => setSurveyJob(e.target.value)}
              placeholder={words.jobPlaceholder}
            />
            <datalist id={`fb-jobs-${jdId}`}>
              {offered.map((t) => <option key={t} value={t} />)}
            </datalist>
          </label>
          {notice && <p className="hint" role="note">{notice}</p>}
          <div className="actions">
            <button type="button" className="btn btn--primary" onClick={() => void send()} disabled={sending || !!reasonHint(reason)}>
              {sending ? 'Sending' : 'Send'}
            </button>
            <button type="button" className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          </div>
          {error && <p className="field__error" role="alert">{error}</p>}
        </section>
      )}

      {history.length > 0 && (
        <section className="card card--pad">
          <h2>{words.history}</h2>
          <ul className="feedback-history">
            {history.map((r) => (
              <li key={r.feedbackId}>
                <p className="subline">
                  {new Date(r.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}{r.by.name}{' · '}<strong>{decisionWords[r.decision]}</strong>
                </p>
                <p className="prose">{r.reason}</p>
                {r.surveyJob && <p className="hint">Named: {r.surveyJob}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

/** Current and new, side by side, with the AI's answer to the reason worded as it wrote it. */
function Choice({ current, next, answer, deciding, onKeepNew, onKeepCurrent, error }: {
  current: Benchmark
  next: Benchmark
  answer: string
  deciding: boolean
  onKeepNew: () => void
  onKeepCurrent: () => void
  error: string | null
}) {
  const c = comparison(current, next)
  return (
    <section className="card card--pad" aria-live="polite">
      <h2>{c.same ? 'Same match' : c.stillUnpriced ? NOT_PRICED.still : 'Choose which match to keep'}</h2>
      {!c.same && (
        <div className="feedback-compare">
          <SideCard label="Current" side={c.current} currency={current.currency} reason={current.noMatchReason} />
          <SideCard label="New" side={c.next} currency={next.currency} reason={next.noMatchReason} highlight />
        </div>
      )}
      <div className="feedback-answer">
        <span className="eyebrow">How your reason was used</span>
        <p className="prose">{answer}</p>
      </div>
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onKeepNew} disabled={deciding}>{keepNewWords(c)}</button>
        <button type="button" className="btn" onClick={onKeepCurrent} disabled={deciding}>Keep the current one</button>
      </div>
      <p className="hint">Until you choose, Salary Ranges and Match a job keep using the current match.</p>
      {error && <p className="field__error" role="alert">{error}</p>}
    </section>
  )
}

function SideCard({ label, side, currency, reason, highlight }: {
  label: string
  side: Side | null
  currency: string | null
  reason?: string | null
  highlight?: boolean
}) {
  return (
    <div className={`feedback-side ${highlight ? 'feedback-side--new' : ''}`}>
      <span className="eyebrow">{label}</span>
      {side ? (
        <>
          <strong>{side.title}</strong>
          <span className="hint">{side.code}</span>
          <span className="hint">Market median</span>
          <span className="feedback-side__figure">{side.median === null ? 'Not published' : money(side.median, currency)}</span>
          {side.matchPercent !== null && <span className="hint">Match {side.matchPercent}%</span>}
          {side.managesPeople !== null && <span className="hint">{side.managesPeople ? 'Manages people' : 'Does the work'}</span>}
        </>
      ) : (
        <>
          <strong>{NOT_PRICED.side}</strong>
          {reason && <span className="hint">{reason}</span>}
        </>
      )}
    </div>
  )
}
